import { takeEvery } from "@redux-saga/core/effects";
import { call, put } from "redux-saga/effects";
import { AxiosError } from "axios";
import { notification } from "antd";

import * as actions from "../Actions/NotificationActions";
import * as api from "../API/NotificationsApi";
import * as types from "../Types/NotificationActionTypes";
import { INotification } from "../Models/Notification";
import { UpdateAllNotificationsReadStatusRequest } from "../Models/Requests/Notifications/UpdateAllNotificationsReadStatusRequest";
import { UpdateAllNotificationsActiveStatusRequest } from "../Models/Requests/Notifications/UpdateAllNotificationsActiveStatusRequest";
import { IUpdateNotificationRequest } from "../Models/Requests/Notifications/UpdateNotificationRequest";
import { GetNotificationsRequest } from "../Models/Requests/Notifications/GetNotificationsRequest";

export function* watchNotificationSagas() {
    yield takeEvery(types.GET_NOTIFICATIONS_INITIATE, getNotificationsSaga);
    yield takeEvery(types.UPDATE_ALL_NOTIFICATIONS_READ_STATUS_INITIATE, updateAllNotificationsReadStatusSaga);
    yield takeEvery(types.UPDATE_ALL_NOTIFICATIONS_ACTIVE_STATUS_INITIATE, updateAllNotificationsActiveStatusSaga);
    yield takeEvery(types.UPDATE_NOTIFICATION_INITIATE, updateNotificationSaga);
    yield takeEvery(types.SET_NOTIFICATIONS_BY_LINK_ID_VIEWED_INITIATE, setNotificationsByLinkIdViewedSaga);
}

export function* setNotificationsByLinkIdViewedSaga(action: types.NotificationActionType) {
    try {
        yield put(actions.setLoadingNotifications(true));
        const linkId = action.data as string;
        yield call(() => api.setViewedByLinkId(linkId));
        yield put(actions.setNotificationsByLinkIdViewedSuccess(linkId));
    } catch (e) {
        const err: AxiosError = e;
        notification.error({
            message: "Error with this notification",
            description: err.message,
        });
        yield put(actions.setNotificationsByLinkIdViewedFailure(err.message));
    } finally {
        yield put(actions.setLoadingNotifications(false));
    }
}

export function* getNotificationsSaga(action: types.NotificationActionType) {
    try {
        yield put(actions.setLoadingNotifications(true));
        const dateRange = action.data as [Date, Date];
        const request = new GetNotificationsRequest(
            dateRange[0],
            dateRange[1]
        );
        const notifications: INotification[] = yield call(() => api.getNotifications(request));
        yield put(actions.getNotificationsSuccess(getFixedNotifications(notifications)));
    } catch (e) {
        const err: AxiosError = e;
        notification.error({
            message: "Error getting Notifications",
            description: err.message,
        });
        yield put(actions.getNotificationsFailure(err.message));
    } finally {
        yield put(actions.setLoadingNotifications(false));
    }
}

export function* updateAllNotificationsReadStatusSaga(action: types.UpdateAllNotificationsActiveStatusInitiate) {
    try {
        yield put(actions.setLoadingNotifications(true));
        const request = new UpdateAllNotificationsReadStatusRequest(action.data);
        yield call(() => api.updateAllNotificationsReadStatus(request));
        yield put(actions.updateAllNotificationsReadStatusSuccess(action.data));
    } catch (e) {
        const err: AxiosError = e;
        notification.error({
            message: "Error updating Read Status for Notifications",
            description: err.message,
        });
        yield put(actions.updateAllNotificationsReadStatusFailure(err.message));
    } finally {
        yield put(actions.setLoadingNotifications(false));
    }
}

export function* updateAllNotificationsActiveStatusSaga(action: types.UpdateAllNotificationsReadStatusInitiate) {
    try {
        yield put(actions.setLoadingNotifications(true));
        const request = new UpdateAllNotificationsActiveStatusRequest(action.data);
        yield call(() => api.updateAllNotificationsActiveStatus(request));
        yield put(actions.updateAllNotificationsActiveStatusSuccess(action.data));
    } catch (e) {
        const err: AxiosError = e;
        notification.error({
            message: "Error updating Active Status for Notifications",
            description: err.message,
        });
        yield put(actions.updateAllNotificationsActiveStatusFailure(err.message));
    } finally {
        yield put(actions.setLoadingNotifications(false));
    }
}

export function* updateNotificationSaga(action: types.NotificationActionType) {
    try {
        yield put(actions.setLoadingNotifications(true));
        const request = action.data as IUpdateNotificationRequest;
        yield call(() => api.updateNotification(request));
        yield put(actions.updateNotificationSuccess(request.notification));
    } catch (e) {
        const err: AxiosError = e;
        notification.error({
            message: "Error updating Notification",
            description: err.message,
        });
        yield put(actions.updateNotificationFailure(err.message));
    } finally {
        yield put(actions.setLoadingNotifications(false));
    }
}

function getFixedNotifications(notifications: INotification[]): INotification[]{
    return notifications.map((n: INotification) => {
        return {
            ...n,
            createdDate: new Date(n.createdDate),
            modifiedDate: new Date(n.modifiedDate)
        };
    });
}
